import { CODES_CONTENU_A_EFFACER, dateHeureDeTunis, type ContactDetail } from '@rh/shared';
import { ErreurApi, ErreurAutreCompte, ErreurReseau } from '../api';
import type { EnvoiContact, RefusEnvoi } from './types';

/**
 * Ce que la file fait d'un échec d'envoi (fonctions pures). Seule une réponse
 * JSON de l'API portant un `message` est un refus ; tout le reste est une
 * panne, que la file retente. Une session refusée (401, `SESSION_REVOQUEE`
 * compris) ne touche à rien : les saisies attendent la reconnexion.
 */

export type Classement =
  | { genre: 'panne'; portee: 'reseau' | 'element'; message: string }
  | { genre: 'session' }
  | { genre: 'autre_compte' }
  | { genre: 'a_verifier'; code: string | null; message: string }
  | { genre: 'refus'; refus: Omit<RefusEnvoi, 'etape' | 'le' | 'action'> };

export type Resolution =
  | { genre: 'deja_fait' }
  | { genre: 'refus'; code: string | null; message: string; action: 'envoyer_evaluation_seule' | null };

/** Statuts jugés définitifs pour cette saisie telle qu'elle est. */
const STATUTS_REFUS = new Set([400, 403, 404, 409, 410, 413, 422]);
/** 409 qui peuvent venir de notre propre envoi arrivé sans réponse : on relit le contact. */
const CODES_A_VERIFIER = new Set(['CONTACT_CLOTURE', 'EVALUATION_EXISTANTE']);

function baremeDe(corps: Record<string, unknown>): RefusEnvoi['bareme'] {
  const bareme = corps.bareme as
    | { affiche?: { id?: unknown; version?: unknown }; applicable?: { id?: unknown; version?: unknown } }
    | undefined;
  const affiche = bareme?.affiche;
  const applicable = bareme?.applicable;
  if (
    typeof affiche?.id !== 'string' ||
    !(affiche.version === null || typeof affiche.version === 'number') ||
    typeof applicable?.id !== 'string' ||
    typeof applicable.version !== 'number'
  ) {
    return null;
  }
  return {
    affiche: { id: affiche.id, version: affiche.version as number | null },
    applicable: { id: applicable.id, version: applicable.version },
  };
}

export function classer(erreur: unknown): Classement {
  if (erreur instanceof ErreurAutreCompte) return { genre: 'autre_compte' };
  if (erreur instanceof ErreurReseau) return { genre: 'panne', portee: 'reseau', message: erreur.message };
  if (!(erreur instanceof ErreurApi)) {
    const detail = erreur instanceof Error && erreur.message ? ` : ${erreur.message}` : '';
    return { genre: 'panne', portee: 'element', message: `Erreur inattendue${detail}` };
  }
  const { statut, code, message } = erreur;
  if (code === 'UTILISATEUR_DIFFERENT') return { genre: 'autre_compte' };
  if (statut === 401) return { genre: 'session' };
  if (statut === 409 && (code === null || CODES_A_VERIFIER.has(code))) {
    return { genre: 'a_verifier', code, message };
  }
  // 404 sans code : route absente derrière un intermédiaire, pas un contact disparu.
  if (statut === 404 && code === null) return { genre: 'panne', portee: 'element', message };
  if (STATUTS_REFUS.has(statut)) {
    return {
      genre: 'refus',
      refus: {
        statut,
        code,
        message,
        details: erreur.details ?? null,
        bareme: baremeDe(erreur.corps),
        contenuEfface: code !== null && (CODES_CONTENU_A_EFFACER as readonly string[]).includes(code),
      },
    };
  }
  // 5xx en JSON, statut inattendu : le contact suivant peut partir.
  return { genre: 'panne', portee: 'element', message };
}

/**
 * Un 409 après relecture du contact : notre fin est-elle déjà passée (réponse
 * perdue, autre onglet) ou la saisie arrive-t-elle trop tard ?
 */
export function resoudreParRelecture(
  envoi: EnvoiContact,
  etape: 'enregistrement' | 'fin',
  lu: ContactDetail,
  origine: { code: string | null; message: string },
): Resolution {
  const fin = envoi.fin;
  if (fin && (lu.clotureEnvoiId === fin.envoiId || lu.evaluation?.envoiId === fin.envoiId)) {
    return { genre: 'deja_fait' };
  }
  if (etape === 'enregistrement' && lu.termineLe !== null && lu.evaluation === null && fin?.genre === 'evaluation') {
    return {
      genre: 'refus',
      code: 'CONTACT_CLOTURE',
      message:
        `Ce contact a été clôturé le ${dateHeureDeTunis(lu.termineLe)} sans évaluation : ` +
        'la trame n’a pas été enregistrée. L’évaluation peut encore partir seule.',
      action: 'envoyer_evaluation_seule',
    };
  }
  if (lu.evaluation !== null && fin?.genre === 'evaluation') {
    return {
      genre: 'refus',
      code: 'EVALUATION_EXISTANTE',
      message:
        `Cet entretien a déjà été évalué le ${dateHeureDeTunis(lu.evaluation.evalueeLe)} ` +
        `par ${lu.evaluation.evalueePar} : votre évaluation n’a pas été enregistrée.`,
      action: null,
    };
  }
  if (lu.termineLe !== null) {
    return {
      genre: 'refus',
      code: 'CONTACT_CLOTURE',
      message:
        `Ce contact a été clôturé le ${dateHeureDeTunis(lu.termineLe)} (sur un autre appareil ou par un ` +
        'administrateur) : votre saisie n’a pas été enregistrée.',
      action: null,
    };
  }
  return { genre: 'refus', code: origine.code, message: origine.message, action: null };
}
